// ==========================
// Version 2 — pages/AdminModeration.js
// - Toast-based feedback (no alert())
// - Adds "Open" link to memory
// - Keeps optimistic remove on delete
// - Preserves index/error banner patterns
// ==========================
import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { queryFlags, adminHideMemory, adminUnhideMemory, adminDeleteMemory } from '../db';
import { toast } from '../utils/toast';

const NAVY = '#0A1A33';
const IVORY = '#FDFCF9';
const GOLD = '#C9A44C';
const WARM_GRAY = '#888888';
const BURGUNDY = '#5C1A2B';
const ROSE_CLAY = '#B76E79';

export default function AdminModeration() {
  const { currentUser } = useAuth();
  const [loading, setLoading] = React.useState(true);
  const [rows, setRows] = React.useState([]);
  const [err, setErr] = React.useState('');
  const [busyId, setBusyId] = React.useState(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      const { rows } = await queryFlags({ status: 'open', limitN: 100 });
      setRows(rows);
    } catch (e) {
      const m = String(e?.message || e);
      if (m.includes('The query requires an index')) {
        setErr('This view needs a Firestore composite index. Open the link shown in the console to create it, then reload.');
        // eslint-disable-next-line no-console
        console.error(e);
      } else {
        setErr('Could not load items for moderation.');
        // eslint-disable-next-line no-console
        console.error('[AdminModeration] load failed:', e);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  async function onHide(memoryId, ownerUid) {
    if (!currentUser?.uid || !memoryId) return;
    setBusyId(memoryId);
    try {
      await adminHideMemory({ memoryId, actorUid: currentUser.uid, ownerUid });
      toast.success('Memory hidden (isPublic=false, isMediaPublic=false).');
    } catch (e) {
      toast.error(e?.message || 'Failed to hide memory.');
    } finally {
      setBusyId(null);
    }
  }

  async function onUnhide(memoryId, ownerUid) {
    if (!currentUser?.uid || !memoryId) return;
    setBusyId(memoryId);
    try {
      await adminUnhideMemory({ memoryId, actorUid: currentUser.uid, ownerUid });
      toast.success('Memory unhidden (isPublic=true, isMediaPublic=true).');
    } catch (e) {
      toast.error(e?.message || 'Failed to unhide memory.');
    } finally {
      setBusyId(null);
    }
  }

  async function onDelete(memoryId, ownerUid) {
    if (!currentUser?.uid || !memoryId) return;
    if (!window.confirm('Delete this memory and its media? This cannot be undone.')) return;
    setBusyId(memoryId);
    try {
      await adminDeleteMemory({ memoryId, actorUid: currentUser.uid, ownerUid });
      setRows((prev) => prev.filter((r) => r.memoryId !== memoryId));
      toast.success('Memory deleted.');
    } catch (e) {
      toast.error(e?.message || 'Failed to delete memory.');
    } finally {
      setBusyId(null);
    }
  }

  return (
    <>
      <h1 style={{ margin: '0 0 12px', fontFamily: 'serif', fontSize: 26, color: IVORY }}>
        Moderation
      </h1>

      <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          style={{
            appearance: 'none',
            background: 'transparent',
            color: IVORY,
            border: `1px solid ${GOLD}`,
            padding: '6px 10px',
            borderRadius: 10,
            cursor: 'pointer',
            fontWeight: 700,
          }}
          title="Reload"
        >
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      {err ? <div style={errBox}>{err}</div> : null}

      <div style={listBox}>
        <div style={thead}>
          <div>Created</div>
          <div>Memory</div>
          <div>Reason</div>
          <div>Reporter</div>
          <div>Owner</div>
          <div>Actions</div>
        </div>

        {loading ? (
          <div style={{ color: WARM_GRAY, padding: 10 }}>Loading…</div>
        ) : rows.length === 0 ? (
          <div style={{ color: WARM_GRAY, padding: 10 }}>Nothing to moderate.</div>
        ) : (
          rows.map((f) => {
            const created =
              f?.createdAt?.toDate ? f.createdAt.toDate().toLocaleString() : '—';
            const memId = f.memoryId || '';

            return (
              <div key={f.id} style={trow}>
                <div title={created}>{created}</div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <code style={{ fontSize: 12 }}>{memId || '—'}</code>
                  {memId ? (
                    <Link to={`/m/${memId}`} target="_blank" rel="noopener noreferrer" style={openLink}>
                      Open ↗
                    </Link>
                  ) : null}
                </div>

                <div>{f.reason || '—'}</div>
                <code style={{ fontSize: 12 }}>{f.reporterUid || '—'}</code>
                <code style={{ fontSize: 12 }}>{f.ownerUid || '—'}</code>

                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    style={btnTiny}
                    disabled={busyId === f.memoryId}
                    onClick={() => onHide(f.memoryId, f.ownerUid)}
                    title="Set isPublic=false and isMediaPublic=false"
                  >
                    Hide
                  </button>
                  <button
                    type="button"
                    style={btnTiny}
                    disabled={busyId === f.memoryId}
                    onClick={() => onUnhide(f.memoryId, f.ownerUid)}
                    title="Set isPublic=true and isMediaPublic=true"
                  >
                    Unhide
                  </button>
                  <button
                    type="button"
                    style={{ ...btnTiny, borderColor: ROSE_CLAY, color: BURGUNDY }}
                    disabled={busyId === f.memoryId}
                    onClick={() => onDelete(f.memoryId, f.ownerUid)}
                    title="Delete memory and its media"
                  >
                    Delete
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>
    </>
  );
}

/* styles (match AdminFlags where possible) */
const listBox = { display: 'grid', gap: 6 };
const thead = {
  display: 'grid',
  gridTemplateColumns: '1.4fr 1.6fr 1fr 1.2fr 1.2fr 1.6fr',
  gap: 8,
  fontWeight: 800,
  color: IVORY,
  background: 'rgba(253,252,249,0.06)',
  padding: 10,
  borderRadius: 10,
};
const trow = {
  display: 'grid',
  gridTemplateColumns: '1.4fr 1.6fr 1fr 1.2fr 1.2fr 1.6fr',
  gap: 8,
  background: '#fff',
  color: NAVY,
  padding: 10,
  borderRadius: 10,
  alignItems: 'center',
};
const btnTiny = {
  appearance: 'none',
  background: 'transparent',
  color: NAVY,
  border: `1px solid ${GOLD}`,
  padding: '4px 8px',
  borderRadius: 8,
  cursor: 'pointer',
  fontWeight: 700,
  fontSize: 12,
};
const openLink = {
  color: GOLD,
  textDecoration: 'none',
  fontWeight: 800,
  fontSize: 12,
};
const errBox = {
  color: BURGUNDY,
  background: 'rgba(92,26,43,0.08)',
  border: '1px dashed rgba(183,110,121,0.55)',
  padding: 10,
  borderRadius: 10,
  margin: '8px 0',
};
// ==========================
// End Version 2 — pages/AdminModeration.js
// ==========================
